/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {isBlank} from '../facade/lang';

export class ParserError {
public message: string;
/**
 * @param {?} message
 * @param {?} input
 * @param {?} errLocation
 * @param {?=} ctxLocation
 */
constructor(
      message: string,
public input: string,
public errLocation: string,
public ctxLocation?: any) {
    this.message = `Parser Error: ${message} ${errLocation} [${input}] in ${ctxLocation}`;
  }
}

function ParserError_tsickle_Closure_declarations() {
 /** @type {?} */
ParserError.prototype.message;
 /** @type {?} */
ParserError.prototype.input;
 /** @type {?} */
ParserError.prototype.errLocation;
 /** @type {?} */
ParserError.prototype.ctxLocation;
}


export class ParseSpan {
/**
 * @param {?} start
 * @param {?} end
 */
constructor(public start: number,
public end: number) {}
}

function ParseSpan_tsickle_Closure_declarations() {
 /** @type {?} */
ParseSpan.prototype.start;
 /** @type {?} */
ParseSpan.prototype.end;
}


export class AST {
/**
 * @param {?} span
 */
constructor(public span: ParseSpan) {}
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any = null): any { return null; }
/**
 * @return {?}
 */
toString(): string { return 'AST'; }
}

function AST_tsickle_Closure_declarations() {
 /** @type {?} */
AST.prototype.span;
}

/**
 *  Represents a quoted expression of the form:
  * *
  * quote = prefix `:` uninterpretedExpression
  * prefix = identifier
  * uninterpretedExpression = arbitrary string
  * *
  * A quoted expression is meant to be pre-processed by an AST transformer that
  * converts it into another AST that no longer contains quoted expressions.
  * It is meant to allow third-party developers to extend Angular template
  * expression language. The `uninterpretedExpression` part of the quote is
  * therefore not interpreted by the Angular's own expression parser.
 */
export class Quote extends AST {
/**
 * @param {?} span
 * @param {?} prefix
 * @param {?} uninterpretedExpression
 * @param {?} location
 */
constructor(
      span: ParseSpan,
public prefix: string,
public uninterpretedExpression: string,
public location: any) {
    super(span);
  }
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any = null): any { return visitor.visitQuote(this, context); }
/**
 * @return {?}
 */
toString(): string { return 'Quote'; }
}

function Quote_tsickle_Closure_declarations() {
 /** @type {?} */
Quote.prototype.prefix;
 /** @type {?} */
Quote.prototype.uninterpretedExpression;
 /** @type {?} */
Quote.prototype.location;
}


export class EmptyExpr extends AST {
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any = null) {
    // do nothing
  }
}

export class ImplicitReceiver extends AST {
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any = null): any {
    return visitor.visitImplicitReceiver(this, context);
  }
}
/**
 *  Multiple expressions separated by a semicolon.
 */
export class Chain extends AST {
/**
 * @param {?} span
 * @param {?} expressions
 */
constructor(span: ParseSpan,
public expressions: any[]) { super(span); }
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any = null): any { return visitor.visitChain(this, context); }
}

function Chain_tsickle_Closure_declarations() {
 /** @type {?} */
Chain.prototype.expressions;
}


export class Conditional extends AST {
/**
 * @param {?} span
 * @param {?} condition
 * @param {?} trueExp
 * @param {?} falseExp
 */
constructor(span: ParseSpan,
public condition: AST,
public trueExp: AST,
public falseExp: AST) {
    super(span);
  }
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any = null): any {
    return visitor.visitConditional(this, context);
  }
}

function Conditional_tsickle_Closure_declarations() {
 /** @type {?} */
Conditional.prototype.condition;
 /** @type {?} */
Conditional.prototype.trueExp;
 /** @type {?} */
Conditional.prototype.falseExp;
}


export class PropertyRead extends AST {
/**
 * @param {?} span
 * @param {?} receiver
 * @param {?} name
 */
constructor(span: ParseSpan,
public receiver: AST,
public name: string) { super(span); }
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any = null): any {
    return visitor.visitPropertyRead(this, context);
  }
}

function PropertyRead_tsickle_Closure_declarations() {
 /** @type {?} */
PropertyRead.prototype.receiver;
 /** @type {?} */
PropertyRead.prototype.name;
}


export class PropertyWrite extends AST {
/**
 * @param {?} span
 * @param {?} receiver
 * @param {?} name
 * @param {?} value
 */
constructor(span: ParseSpan,
public receiver: AST,
public name: string,
public value: AST) {
    super(span);
  }
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any = null): any {
    return visitor.visitPropertyWrite(this, context);
  }
}

function PropertyWrite_tsickle_Closure_declarations() {
 /** @type {?} */
PropertyWrite.prototype.receiver;
 /** @type {?} */
PropertyWrite.prototype.name;
 /** @type {?} */
PropertyWrite.prototype.value;
}


export class SafePropertyRead extends AST {
/**
 * @param {?} span
 * @param {?} receiver
 * @param {?} name
 */
constructor(span: ParseSpan,
public receiver: AST,
public name: string) { super(span); }
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any = null): any {
    return visitor.visitSafePropertyRead(this, context);
  }
}

function SafePropertyRead_tsickle_Closure_declarations() {
 /** @type {?} */
SafePropertyRead.prototype.receiver;
 /** @type {?} */
SafePropertyRead.prototype.name;
}


export class KeyedRead extends AST {
/**
 * @param {?} span
 * @param {?} obj
 * @param {?} key
 */
constructor(span: ParseSpan,
public obj: AST,
public key: AST) { super(span); }
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any = null): any {
    return visitor.visitKeyedRead(this, context);
  }
}

function KeyedRead_tsickle_Closure_declarations() {
 /** @type {?} */
KeyedRead.prototype.obj;
 /** @type {?} */
KeyedRead.prototype.key;
}


export class KeyedWrite extends AST {
/**
 * @param {?} span
 * @param {?} obj
 * @param {?} key
 * @param {?} value
 */
constructor(span: ParseSpan,
public obj: AST,
public key: AST,
public value: AST) { super(span); }
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any = null): any {
    return visitor.visitKeyedWrite(this, context);
  }
}

function KeyedWrite_tsickle_Closure_declarations() {
 /** @type {?} */
KeyedWrite.prototype.obj;
 /** @type {?} */
KeyedWrite.prototype.key;
 /** @type {?} */
KeyedWrite.prototype.value;
}


export class BindingPipe extends AST {
/**
 * @param {?} span
 * @param {?} exp
 * @param {?} name
 * @param {?} args
 */
constructor(span: ParseSpan,
public exp: AST,
public name: string,
public args: any[]) {
    super(span);
  }
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any = null): any { return visitor.visitPipe(this, context); }
}

function BindingPipe_tsickle_Closure_declarations() {
 /** @type {?} */
BindingPipe.prototype.exp;
 /** @type {?} */
BindingPipe.prototype.name;
 /** @type {?} */
BindingPipe.prototype.args;
}


export class LiteralPrimitive extends AST {
/**
 * @param {?} span
 * @param {?} value
 */
constructor(span: ParseSpan,
public value: any) { super(span); }
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any = null): any {
    return visitor.visitLiteralPrimitive(this, context);
  }
}

function LiteralPrimitive_tsickle_Closure_declarations() {
 /** @type {?} */
LiteralPrimitive.prototype.value;
}


export class LiteralArray extends AST {
/**
 * @param {?} span
 * @param {?} expressions
 */
constructor(span: ParseSpan,
public expressions: any[]) { super(span); }
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any = null): any {
    return visitor.visitLiteralArray(this, context);
  }
}

function LiteralArray_tsickle_Closure_declarations() {
 /** @type {?} */
LiteralArray.prototype.expressions;
}


export class LiteralMap extends AST {
/**
 * @param {?} span
 * @param {?} keys
 * @param {?} values
 */
constructor(span: ParseSpan,
public keys: any[],
public values: any[]) { super(span); }
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any = null): any {
    return visitor.visitLiteralMap(this, context);
  }
}

function LiteralMap_tsickle_Closure_declarations() {
 /** @type {?} */
LiteralMap.prototype.keys;
 /** @type {?} */
LiteralMap.prototype.values;
}


export class Interpolation extends AST {
/**
 * @param {?} span
 * @param {?} strings
 * @param {?} expressions
 */
constructor(span: ParseSpan,
public strings: any[],
public expressions: any[]) { super(span); }
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any = null): any {
    return visitor.visitInterpolation(this, context);
  }
}

function Interpolation_tsickle_Closure_declarations() {
 /** @type {?} */
Interpolation.prototype.strings;
 /** @type {?} */
Interpolation.prototype.expressions;
}


export class Binary extends AST {
/**
 * @param {?} span
 * @param {?} operation
 * @param {?} left
 * @param {?} right
 */
constructor(span: ParseSpan,
public operation: string,
public left: AST,
public right: AST) {
    super(span);
  }
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any = null): any {
    return visitor.visitBinary(this, context);
  }
}

function Binary_tsickle_Closure_declarations() {
 /** @type {?} */
Binary.prototype.operation;
 /** @type {?} */
Binary.prototype.left;
 /** @type {?} */
Binary.prototype.right;
}


export class PrefixNot extends AST {
/**
 * @param {?} span
 * @param {?} expression
 */
constructor(span: ParseSpan,
public expression: AST) { super(span); }
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any = null): any {
    return visitor.visitPrefixNot(this, context);
  }
}

function PrefixNot_tsickle_Closure_declarations() {
 /** @type {?} */
PrefixNot.prototype.expression;
}


export class MethodCall extends AST {
/**
 * @param {?} span
 * @param {?} receiver
 * @param {?} name
 * @param {?} args
 */
constructor(span: ParseSpan,
public receiver: AST,
public name: string,
public args: any[]) {
    super(span);
  }
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any = null): any {
    return visitor.visitMethodCall(this, context);
  }
}

function MethodCall_tsickle_Closure_declarations() {
 /** @type {?} */
MethodCall.prototype.receiver;
 /** @type {?} */
MethodCall.prototype.name;
 /** @type {?} */
MethodCall.prototype.args;
}


export class SafeMethodCall extends AST {
/**
 * @param {?} span
 * @param {?} receiver
 * @param {?} name
 * @param {?} args
 */
constructor(span: ParseSpan,
public receiver: AST,
public name: string,
public args: any[]) {
    super(span);
  }
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any = null): any {
    return visitor.visitSafeMethodCall(this, context);
  }
}

function SafeMethodCall_tsickle_Closure_declarations() {
 /** @type {?} */
SafeMethodCall.prototype.receiver;
 /** @type {?} */
SafeMethodCall.prototype.name;
 /** @type {?} */
SafeMethodCall.prototype.args;
}


export class FunctionCall extends AST {
/**
 * @param {?} span
 * @param {?} target
 * @param {?} args
 */
constructor(span: ParseSpan,
public target: AST,
public args: any[]) { super(span); }
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any = null): any {
    return visitor.visitFunctionCall(this, context);
  }
}

function FunctionCall_tsickle_Closure_declarations() {
 /** @type {?} */
FunctionCall.prototype.target;
 /** @type {?} */
FunctionCall.prototype.args;
}


export class ASTWithSource extends AST {
/**
 * @param {?} ast
 * @param {?} source
 * @param {?} location
 * @param {?} errors
 */
constructor(
public ast: AST,
public source: string,
public location: string,
public errors: ParserError[]) {
    super(new ParseSpan(0, isBlank(source) ? 0 : source.length));
  }
/**
 * @param {?} visitor
 * @param {?=} context
 * @return {?}
 */
visit(visitor: AstVisitor, context: any = null): any { return this.ast.visit(visitor, context); }
/**
 * @return {?}
 */
toString(): string { return `${this.source} in ${this.location}`; }
}

function ASTWithSource_tsickle_Closure_declarations() {
 /** @type {?} */
ASTWithSource.prototype.ast;
 /** @type {?} */
ASTWithSource.prototype.source;
 /** @type {?} */
ASTWithSource.prototype.location;
 /** @type {?} */
ASTWithSource.prototype.errors;
}


export class TemplateBinding {
/**
 * @param {?} span
 * @param {?} key
 * @param {?} keyIsVar
 * @param {?} name
 * @param {?} expression
 */
constructor(
public span: ParseSpan,
public key: string,
public keyIsVar: boolean,
public name: string,
public expression: ASTWithSource) {}
}

function TemplateBinding_tsickle_Closure_declarations() {
 /** @type {?} */
TemplateBinding.prototype.span;
 /** @type {?} */
TemplateBinding.prototype.key;
 /** @type {?} */
TemplateBinding.prototype.keyIsVar;
 /** @type {?} */
TemplateBinding.prototype.name;
 /** @type {?} */
TemplateBinding.prototype.expression;
}


export interface AstVisitor {
  visitBinary(ast: Binary, context: any): any;
  visitChain(ast: Chain, context: any): any;
  visitConditional(ast: Conditional, context: any): any;
  visitFunctionCall(ast: FunctionCall, context: any): any;
  visitImplicitReceiver(ast: ImplicitReceiver, context: any): any;
  visitInterpolation(ast: Interpolation, context: any): any;
  visitKeyedRead(ast: KeyedRead, context: any): any;
  visitKeyedWrite(ast: KeyedWrite, context: any): any;
  visitLiteralArray(ast: LiteralArray, context: any): any;
  visitLiteralMap(ast: LiteralMap, context: any): any;
  visitLiteralPrimitive(ast: LiteralPrimitive, context: any): any;
  visitMethodCall(ast: MethodCall, context: any): any;
  visitPipe(ast: BindingPipe, context: any): any;
  visitPrefixNot(ast: PrefixNot, context: any): any;
  visitPropertyRead(ast: PropertyRead, context: any): any;
  visitPropertyWrite(ast: PropertyWrite, context: any): any;
  visitQuote(ast: Quote, context: any): any;
  visitSafeMethodCall(ast: SafeMethodCall, context: any): any;
  visitSafePropertyRead(ast: SafePropertyRead, context: any): any;
}

export class RecursiveAstVisitor implements AstVisitor {
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitBinary(ast: Binary, context: any): any {
    ast.left.visit(this);
    ast.right.visit(this);
    return null;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitChain(ast: Chain, context: any): any { return this.visitAll(ast.expressions, context); }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitConditional(ast: Conditional, context: any): any {
    ast.condition.visit(this);
    ast.trueExp.visit(this);
    ast.falseExp.visit(this);
    return null;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitPipe(ast: BindingPipe, context: any): any {
    ast.exp.visit(this);
    this.visitAll(ast.args, context);
    return null;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitFunctionCall(ast: FunctionCall, context: any): any {
    ast.target.visit(this);
    this.visitAll(ast.args, context);
    return null;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitImplicitReceiver(ast: ImplicitReceiver, context: any): any { return null; }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitInterpolation(ast: Interpolation, context: any): any {
    return this.visitAll(ast.expressions, context);
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitKeyedRead(ast: KeyedRead, context: any): any {
    ast.obj.visit(this);
    ast.key.visit(this);
    return null;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitKeyedWrite(ast: KeyedWrite, context: any): any {
    ast.obj.visit(this);
    ast.key.visit(this);
    ast.value.visit(this);
    return null;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitLiteralArray(ast: LiteralArray, context: any): any {
    return this.visitAll(ast.expressions, context);
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitLiteralMap(ast: LiteralMap, context: any): any { return this.visitAll(ast.values, context); }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitLiteralPrimitive(ast: LiteralPrimitive, context: any): any { return null; }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitMethodCall(ast: MethodCall, context: any): any {
    ast.receiver.visit(this);
    return this.visitAll(ast.args, context);
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitPrefixNot(ast: PrefixNot, context: any): any {
    ast.expression.visit(this);
    return null;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitPropertyRead(ast: PropertyRead, context: any): any {
    ast.receiver.visit(this);
    return null;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitPropertyWrite(ast: PropertyWrite, context: any): any {
    ast.receiver.visit(this);
    ast.value.visit(this);
    return null;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitSafePropertyRead(ast: SafePropertyRead, context: any): any {
    ast.receiver.visit(this);
    return null;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitSafeMethodCall(ast: SafeMethodCall, context: any): any {
    ast.receiver.visit(this);
    return this.visitAll(ast.args, context);
  }
/**
 * @param {?} asts
 * @param {?} context
 * @return {?}
 */
visitAll(asts: AST[], context: any): any {
    asts.forEach(ast => ast.visit(this, context));
    return null;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitQuote(ast: Quote, context: any): any { return null; }
}

export class AstTransformer implements AstVisitor {
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitImplicitReceiver(ast: ImplicitReceiver, context: any): AST { return ast; }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitInterpolation(ast: Interpolation, context: any): AST {
    return new Interpolation(ast.span, ast.strings, this.visitAll(ast.expressions));
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitLiteralPrimitive(ast: LiteralPrimitive, context: any): AST {
    return new LiteralPrimitive(ast.span, ast.value);
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitPropertyRead(ast: PropertyRead, context: any): AST {
    return new PropertyRead(ast.span, ast.receiver.visit(this), ast.name);
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitPropertyWrite(ast: PropertyWrite, context: any): AST {
    return new PropertyWrite(ast.span, ast.receiver.visit(this), ast.name, ast.value);
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitSafePropertyRead(ast: SafePropertyRead, context: any): AST {
    return new SafePropertyRead(ast.span, ast.receiver.visit(this), ast.name);
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitMethodCall(ast: MethodCall, context: any): AST {
    return new MethodCall(ast.span, ast.receiver.visit(this), ast.name, this.visitAll(ast.args));
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitSafeMethodCall(ast: SafeMethodCall, context: any): AST {
    return new SafeMethodCall(
        ast.span, ast.receiver.visit(this), ast.name, this.visitAll(ast.args));
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitFunctionCall(ast: FunctionCall, context: any): AST {
    return new FunctionCall(ast.span, ast.target.visit(this), this.visitAll(ast.args));
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitLiteralArray(ast: LiteralArray, context: any): AST {
    return new LiteralArray(ast.span, this.visitAll(ast.expressions));
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitLiteralMap(ast: LiteralMap, context: any): AST {
    return new LiteralMap(ast.span, ast.keys, this.visitAll(ast.values));
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitBinary(ast: Binary, context: any): AST {
    return new Binary(ast.span, ast.operation, ast.left.visit(this), ast.right.visit(this));
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitPrefixNot(ast: PrefixNot, context: any): AST {
    return new PrefixNot(ast.span, ast.expression.visit(this));
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitConditional(ast: Conditional, context: any): AST {
    return new Conditional(
        ast.span, ast.condition.visit(this), ast.trueExp.visit(this), ast.falseExp.visit(this));
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitPipe(ast: BindingPipe, context: any): AST {
    return new BindingPipe(ast.span, ast.exp.visit(this), ast.name, this.visitAll(ast.args));
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitKeyedRead(ast: KeyedRead, context: any): AST {
    return new KeyedRead(ast.span, ast.obj.visit(this), ast.key.visit(this));
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitKeyedWrite(ast: KeyedWrite, context: any): AST {
    return new KeyedWrite(
        ast.span, ast.obj.visit(this), ast.key.visit(this), ast.value.visit(this));
  }
/**
 * @param {?} asts
 * @return {?}
 */
visitAll(asts: any[]): any[] {
    const /** @type {?} */ res = new Array(asts.length);
    for (let /** @type {?} */ i = 0; i < asts.length; ++i) {
      res[i] = asts[i].visit(this);
    }
    return res;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitChain(ast: Chain, context: any): AST {
    return new Chain(ast.span, this.visitAll(ast.expressions));
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitQuote(ast: Quote, context: any): AST {
    return new Quote(ast.span, ast.prefix, ast.uninterpretedExpression, ast.location);
  }
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
